<template>
    <LayoutBase class="registration-info-edit">
        <van-form class="my-info-form" validate-first @submit="onSubmit" ref="form" :readonly="readOnly">
            <template v-if="showNationalityType && !isInternalUser && (newRegistrationInfoEnabled || isForeign)">
                <EnumPicker
                    :label="$t('registration.highestEducation')"
                    :options="$constants.HIGHEST_EDUCATION"
                    i18n-prefix="common.highestEducationEnum"
                    :rules="formRules.required"
                    v-model="form.highestEducation"
                    right-icon="arrow"
                    :read-only="readOnly"
                />
                <van-field
                    :label="$t('registration.healthStatus')"
                    v-model="form.healthStatus"
                    :placeholder="$t('common.placeholder.input')"
                    :rules="formRules.required"
                    :error="false"
                    :maxlength="20"
                />
                <template v-if="isForeign">
                    <van-field
                        :label="$t('registration.chineseLevel')"
                        v-model="form.chineseLevel"
                        :placeholder="$t('common.placeholder.input')"
                        :rules="formRules.required"
                        :error="false"
                        :maxlength="20"
                    />
                    <van-field
                        :label="$t('registration.company')"
                        v-model="form.company"
                        :placeholder="$t('common.placeholder.input')"
                        :rules="formRules.required"
                        :error="false"
                        :maxlength="100"
                    />
                    <van-field
                        :label="$t('registration.address')"
                        v-model="form.address"
                        :placeholder="$t('common.placeholder.input')"
                        :rules="formRules.required"
                        :error="false"
                        :maxlength="100"
                    />
                    <van-field
                        :label="$t('registration.faxNumber')"
                        v-model="form.fax"
                        :placeholder="$t('common.placeholder.input')"
                        :rules="formRules.required"
                        :error="false"
                        :maxlength="20"
                    />
                    <van-field
                        :label="$t('registration.contactPhone')"
                        v-model="form.contactPhone"
                        :placeholder="$t('common.placeholder.input')"
                        :rules="formRules.required"
                        :error="false"
                        :maxlength="100"
                    />
                    <Contacts
                        :label="$t('registration.contacts')"
                        :placeholder="$t('common.placeholder.add')"
                        :error="false"
                        :contacts="form.contacts"
                        @change="contactsChange"
                        :more="true"
                        @delete="deleteContact"
                        :read-only="readOnly"
                    />
                    <template v-if="newRegistrationInfoEnabled">
                        <Contacts
                            class="recommending-company"
                            :label="$t('registration.recommendingCompanyAndPhone')"
                            :placeholder="$t('common.placeholder.add')"
                            :error="false"
                            :contacts="recommendingCompanyContacts"
                            @change="recommendingCompanyContactsChange"
                            :name-label="$t('registration.recommendingCompany')"
                            :read-only="readOnly"
                        />
                    </template>
                    <Contacts
                        class="guarantor"
                        :label="$t('registration.guarantorAndPhone')"
                        :placeholder="$t('common.placeholder.add')"
                        :error="false"
                        :contacts="guarantorContacts"
                        @change="guarantorContactsChange"
                        :name-label="$t('registration.guarantor')"
                        :read-only="readOnly"
                    />
                    <template v-if="newRegistrationInfoEnabled">
                        <van-field
                            :label="$t('registration.studyMajor')"
                            v-model="form.major"
                            :placeholder="$t('common.placeholder.input')"
                            :rules="formRules.required"
                            :error="false"
                            class="study-major"
                        />
                        <van-field
                            :label="$t('registration.studentType')"
                            v-model="form.studentType"
                            :placeholder="$t('common.placeholder.input')"
                            :rules="formRules.required"
                            :error="false"
                        />
                        <DateRangeTimePicker
                            :label="$t('registration.studyDuration')"
                            :start-date-text="form.studyStartDate"
                            :end-date-text="form.studyEndDate"
                            @change="studyDurationChange"
                            :read-only="readOnly"
                        />
                        <EnumPicker
                            :label="$t('registration.fundingSource')"
                            :options="$constants.FUNDING_SOURCE"
                            i18n-prefix="common.fundingSourceEnum"
                            :rules="formRules.required"
                            v-model="form.fundingSource"
                            right-icon="arrow"
                            :read-only="readOnly"
                        />
                    </template>
                </template>
                <template v-if="newRegistrationInfoEnabled">
                    <van-field
                        show-word-limit
                        rows="2"
                        type="textarea"
                        :label="$t('registration.tcmBackground')"
                        v-model="form.tcmBackground"
                        :placeholder="$t('common.placeholder.input')"
                        :rules="formRules.required"
                        :error="false"
                        :maxlength="200"
                    />
                    <van-field
                        show-word-limit
                        rows="2"
                        type="textarea"
                        :label="$t('registration.studyPlan')"
                        v-model="form.studyPlan"
                        :placeholder="$t('common.placeholder.input')"
                        :rules="formRules.required"
                        :error="false"
                        :maxlength="200"
                    />
                </template>
            </template>
            <VanTabbar class="submit-tabbar" v-if="!readOnly">
                <van-button
                    class="submit-button"
                    block
                    type="info"
                    native-type="submit"
                    :loading="loading"
                    :disabled="submitDisabled"
                >
                    {{ $t("common.btn.commitAndRegistration") }}
                </van-button>
            </VanTabbar>
        </van-form>
    </LayoutBase>
</template>

<script>
import Vue from "vue";
import EnumPicker from "@/components/picker/EnumPicker.vue";
import DateRangeTimePicker from "@/components/picker/DateRangeTimePicker.vue";
import Contacts from "@/components/input/Contacts.vue";
import { NationalityType } from "@/scripts/enums";
import { currentStudentBasicInfo, currentStudentIdentityInfo } from "@/api/my";
import _ from "lodash";
import { createRegistration, registrationInfo, updateRegistration } from "@/api/plan";
import { createNamespacedHelpers } from "vuex";

const { mapGetters, mapActions } = createNamespacedHelpers("config");

export default Vue.extend({
    name: "RegistrationInfoEdit",
    props: {
        planId: {
            type: Number,
            required: true
        },
        readOnly: {
            type: Boolean,
            default: false
        }
    },
    components: { EnumPicker, Contacts, DateRangeTimePicker },
    data() {
        return {
            form: {
                highestEducation: "",
                healthStatus: "",
                chineseLevel: null,
                company: null,
                fax: null,
                address: null,
                recommendingCompany: "",
                recommendingCompanyPhone: "",
                guarantor: "",
                guarantorPhone: "",
                studentType: null,
                studyStartDate: null,
                studyEndDate: null,
                fundingSource: null,
                tcmBackground: null,
                studyPlan: null,
                contacts: [],
                contactPhone: null,
                major: null,
                attachments: []
            },
            recommendingCompanyContacts: [],
            guarantorContacts: [],
            formRules: {
                required: [{ required: true, message: this.$t("common.error.required") }]
            },
            formErrors: {
                identityNo: ""
            },
            loading: false,
            isForeign: false,
            isInternalUser: true,
            submitDisabled: false,
            isUpdate: false
        };
    },
    computed: {
        ...mapGetters(["orgConfig"]),
        showNationalityType() {
            if (_.keys(this.orgConfig).length == 0) {
                return false;
            }
            return !!this.orgConfig["showNationalityType"];
        },
        newRegistrationInfoEnabled() {
            if (_.keys(this.orgConfig).length == 0) {
                return false;
            }
            return !!this.orgConfig["newRegistrationInfoEnabled"];
        }
    },
    methods: {
        ...mapActions(["getOrgConfig"]),
        contactsChange(item) {
            let contacts = [];
            if (this.form.contacts && this.form.contacts.length > 0 && item.more) {
                contacts = [...this.form.contacts];
            }
            if (item.isEdit) {
                contacts[item.index] = { name: item.name, phone: item.phone };
                return this.$set(this.form, "contacts", contacts);
            }
            contacts.push({ name: item.name, phone: item.phone });
            return this.$set(this.form, "contacts", contacts);
        },
        deleteContact(inx) {
            const contacts = [...this.form.contacts];
            contacts.splice(inx, 1);
            return this.$set(this.form, "contacts", contacts);
        },
        recommendingCompanyContactsChange(item) {
            this.$set(this, "recommendingCompanyContacts", [{ name: item.name, phone: item.phone }]);
            this.$set(this.form, "recommendingCompany", item.name);
            this.$set(this.form, "recommendingCompanyPhone", item.phone);
        },
        guarantorContactsChange(item) {
            this.$set(this, "guarantorContacts", [{ name: item.name, phone: item.phone }]);
            this.$set(this.form, "guarantor", item.name);
            this.$set(this.form, "guarantorPhone", item.phone);
        },
        initIdPhotoAttachments(photo) {
            return [
                {
                    code: "id-photo",
                    files: [photo.fileKey]
                }
            ];
        },
        onSubmit() {
            this.$refs.form
                .validate()
                .catch(error => {
                    console.log(error);
                })
                .then(success => {
                    if (this.isUpdate) {
                        this.form.attachments = _.map(this.form.attachments, attachment => {
                            return {
                                code: attachment.code,
                                files: _.map(attachment.resource, "fileKey")
                            };
                        });
                        updateRegistration(this.planId, this.form)
                            .then(ignore => {
                                this.$toast(this.$t("common.alert.saveSuccess"));
                                this.$router.back();
                            })
                            .catch(errors => {
                                _.forEach(errors, error => {
                                    if (error.field === "identityNo") {
                                        this.formErrors.identityNo = error.message;
                                    }
                                });
                            });
                    } else {
                        createRegistration(this.planId, this.form)
                            .then(ignore => {
                                this.$toast(this.$t("common.alert.saveSuccess"));
                                this.$router.back();
                            })
                            .catch(errors => {
                                _.forEach(errors, error => {
                                    if (error.field === "identityNo") {
                                        this.formErrors.identityNo = error.message;
                                    }
                                });
                            });
                    }
                });
        },
        studyDurationChange(vals) {
            this.$set(this.form, "studyStartDate", vals[0]);
            this.$set(this.form, "studyEndDate", vals[1]);
        }
    },
    async mounted() {
        const basicInfoResp = await currentStudentBasicInfo();
        const registrationInfoResp = await registrationInfo(this.planId);
        const identityInfoResp = await currentStudentIdentityInfo();
        this.isUpdate = _.keys(registrationInfoResp).length > 0;
        this.form = { ...this.form, ...registrationInfoResp, ...identityInfoResp.data.data };
        if (!this.isUpdate) {
            this.form.attachments = this.initIdPhotoAttachments(this.form.idPhoto);
        }
        this.isForeign = basicInfoResp.data.data.nationalityType === NationalityType.FOREIGN;
        this.isInternalUser = basicInfoResp.data.data.isInternalUser;
        this.submitDisabled = false;
        if (this.form.recommendingCompany && this.form.recommendingCompanyPhone) {
            this.recommendingCompanyContacts = [
                { name: this.form.recommendingCompany, phone: this.form.recommendingCompanyPhone }
            ];
        }
        if (this.form.guarantor && this.form.guarantorPhone) {
            this.guarantorContacts = [{ name: this.form.guarantor, phone: this.form.guarantorPhone }];
        }
        await this.getOrgConfig();
    }
});
</script>

<style scoped lang="less">
.registration-info-edit {
    height: 100%;
    /deep/ .van-field__control {
        text-align: right;
    }
    .my-info-form {
        display: flex;
        padding: 16px 8px 82px 8px;
        flex-direction: column;

        .study-major,
        .guarantor,
        .recommending-company {
            margin-top: 8px;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
        }

        .email {
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
        }
        .submit-tabbar {
            display: flex;
            padding: 8px;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
            background: #fff;
            box-shadow: 0 3px 10px 0 rgba(38, 40, 51, 0.1), 0 0 1px 0 rgba(38, 40, 51, 0.1);

            .submit-button {
                width: calc(100% - 16px);
            }
        }
    }
}
</style>
